import React, { useState, useEffect } from 'react';
import { TrendingUp, Calendar, Clock, Target, TrendingDown, Activity, BarChart3, LineChart, PieChart, Filter, MessageCircle } from 'lucide-react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { enhancedDashboardQueries } from '../../services/enhancedDashboardQueries';
import axios from 'axios';

const TimeTrends = ({ dashboardData, filters, onFilterChange, accountId }) => {
  const [selectedMetrics, setSelectedMetrics] = useState(['ctr', 'roas', 'spend']);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
  const [selectedGrouping, setSelectedGrouping] = useState('day');
  const [viewMode, setViewMode] = useState('trends'); // 'trends', 'comparison'
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const timeframes = [
    { key: '7d', label: '7 Days', days: 7 },
    { key: '14d', label: '14 Days', days: 14 },
    { key: '30d', label: '30 Days', days: 30 },
    { key: '90d', label: '90 Days', days: 90 },
    { key: '180d', label: '180 Days', days: 180 },
    { key: '365d', label: '1 Year', days: 365 },
    { key: '730d', label: '2 Years', days: 730 },
    { key: '1095d', label: '3 Years', days: 1095 }
  ];

  const groupingOptions = [
    { key: 'day', label: 'Daily', icon: '📅' },
    { key: 'week', label: 'Weekly', icon: '📊' },
    { key: 'month', label: 'Monthly', icon: '📈' }
  ];

  const availableMetrics = [
    { key: 'ctr', label: 'CTR (%)', icon: '📊', format: 'percentage', color: '#10B981' },
    { key: 'cvr', label: 'CVR (%)', icon: '📉', format: 'percentage', color: '#8B5CF6' },
    { key: 'roas', label: 'ROAS (x)', icon: '🚀', format: 'multiplier', color: '#3B82F6' },
    { key: 'cpa', label: 'CPA ($)', icon: '💸', format: 'currency', color: '#EF4444' },
    { key: 'cpm', label: 'CPM ($)', icon: '📈', format: 'currency', color: '#F59E0B' },
    { key: 'spend', label: 'Spend ($)', icon: '💰', format: 'currency', color: '#06B6D4' },
    { key: 'impressions', label: 'Impressions', icon: '👁️', format: 'number', color: '#84CC16' },
    { key: 'clicks', label: 'Clicks', icon: '🖱️', format: 'number', color: '#EC4899' }
  ];

  const viewModes = [
    { key: 'trends', label: 'Trends', icon: <TrendingUp className="h-4 w-4" /> },
    { key: 'comparison', label: 'Comparison', icon: <Target className="h-4 w-4" /> }
  ];

  // Calculate date range based on selected timeframe
  const getDateRange = () => {
    const selected = timeframes.find(t => t.key === selectedTimeframe);
    if (!selected) return { startDate: '', endDate: '' };
    
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - selected.days);
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  // Load time series data using real SQL query
  const loadTimeSeriesData = async () => {
    console.log('TimeTrends: loadTimeSeriesData called');
    console.log('TimeTrends: accountId:', accountId);
    
    if (!accountId) {
      console.log('TimeTrends: No accountId, returning early');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const { startDate, endDate } = getDateRange();
      console.log('TimeTrends: Date range calculated:', { startDate, endDate });
      
      // Check if enhancedDashboardQueries is available
      if (!enhancedDashboardQueries || !enhancedDashboardQueries.getTimeTrendsData) {
        console.error('TimeTrends: enhancedDashboardQueries not available:', enhancedDashboardQueries);
        throw new Error('Enhanced dashboard queries service not available');
      }
      
      const query = enhancedDashboardQueries.getTimeTrendsData(
        accountId, 
        startDate, 
        endDate, 
        selectedMetrics, 
        selectedGrouping
      );
      
      console.log('TimeTrends: Query generated:', query);
      
      // Execute the SQL query through the chat API
      const response = await axios.post('/api/chat/message', { 
        message: query, 
        accountId: accountId 
      });
      
      console.log('TimeTrends: API response:', response.data);
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        console.log('TimeTrends: Raw database data:', response.data.data);
        console.log('TimeTrends: First row sample:', response.data.data[0]);
        
        // Process the real data from the database
        const processedData = response.data.data.map(row => ({
          period: row.period,
          // Map the actual SQL field names to the expected chart field names
          impressions: parseFloat(row.total_impressions) || 0,
          clicks: parseFloat(row.total_clicks) || 0,
          spend: parseFloat(row.total_spend) || 0,
          conversions: parseFloat(row.total_conversions) || 0,
          revenue: parseFloat(row.total_revenue) || 0,
          // Fix magnitude issues - SQL already returns percentages, don't multiply by 100 again
          ctr: parseFloat(row.ctr) || 0,
          cvr: parseFloat(row.cvr) || 0,
          roas: parseFloat(row.roas) || 0,
          cpa: parseFloat(row.cpa) || 0,
          cpm: parseFloat(row.cpm) || 0,
          active_ads: parseInt(row.active_ads) || 0
        }));
        
        console.log('TimeTrends: Processed data:', processedData);
        console.log('TimeTrends: First processed row:', processedData[0]);
        setTimeSeriesData(processedData);
      } else {
        console.log('TimeTrends: No real data, using sample data');
        // Fallback to sample data for demonstration
        const sampleData = generateSampleTimeSeriesData(startDate, endDate, selectedGrouping, selectedMetrics);
        setTimeSeriesData(sampleData);
        setError('Using sample data - real data not available');
      }
    } catch (err) {
      console.error('TimeTrends: Error loading data:', err);
      // Fallback to sample data on error
      const { startDate, endDate } = getDateRange();
      const sampleData = generateSampleTimeSeriesData(startDate, endDate, selectedGrouping, selectedMetrics);
      setTimeSeriesData(sampleData);
      setError('Using sample data due to error: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Generate sample data for demonstration when real data is not available
  const generateSampleTimeSeriesData = (startDate, endDate, grouping, metrics) => {
    const data = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    let current = new Date(start);
    
    while (current <= end) {
      const period = grouping === 'day' ? current.toISOString().split('T')[0] :
                    grouping === 'week' ? getWeekStart(current).toISOString().split('T')[0] :
                    getMonthStart(current).toISOString().split('T')[0];
      
      const periodData = { period };
      
      // Generate realistic sample data for each metric
      metrics.forEach(metric => {
        switch (metric) {
          case 'ctr':
            periodData[metric] = 1.5 + (Math.random() * 1.0) + (Math.sin(current.getTime() / 86400000) * 0.5);
            break;
          case 'cvr':
            periodData[metric] = 5.0 + (Math.random() * 3.0) + (Math.sin(current.getTime() / 86400000) * 1.0);
            break;
          case 'roas':
            periodData[metric] = 2.5 + (Math.random() * 1.5) + (Math.sin(current.getTime() / 86400000) * 0.3);
            break;
          case 'cpa':
            periodData[metric] = 12 + (Math.random() * 8) + (Math.sin(current.getTime() / 86400000) * 2);
            break;
          case 'cpm':
            periodData[metric] = 8 + (Math.random() * 6) + (Math.sin(current.getTime() / 86400000) * 1.5);
            break;
          case 'spend':
            periodData[metric] = 500 + (Math.random() * 300) + (Math.sin(current.getTime() / 86400000) * 100);
            break;
          case 'impressions':
            periodData[metric] = 50000 + (Math.random() * 30000) + (Math.sin(current.getTime() / 86400000) * 10000);
            break;
          case 'clicks':
            periodData[metric] = 750 + (Math.random() * 450) + (Math.sin(current.getTime() / 86400000) * 150);
            break;
        }
      });
      
      data.push(periodData);
      
      // Move to next period
      if (grouping === 'day') {
        current.setDate(current.getDate() + 1);
      } else if (grouping === 'week') {
        current.setDate(current.getDate() + 7);
      } else {
        current.setMonth(current.getMonth() + 1);
      }
    }
    
    return data;
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const getMonthStart = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  // Generate contextual chat prompts based on selected metric and time period
  const generateChatPrompt = (metric, period = null) => {
    const metricLabels = {
      'ctr': 'CTR',
      'cvr': 'CVR', 
      'roas': 'ROAS',
      'cpa': 'CPA',
      'cpm': 'CPM',
      'spend': 'spend',
      'impressions': 'impressions',
      'clicks': 'clicks'
    };

    const metricLabel = metricLabels[metric] || metric;
    const timeframeLabel = timeframes.find(t => t.key === selectedTimeframe)?.label || selectedTimeframe;
    const groupingLabel = groupingOptions.find(g => g.key === selectedGrouping)?.label || selectedGrouping;

    // Include all selected metrics in the prompt for context
    const selectedMetricsList = selectedMetrics.map(m => metricLabels[m] || m).join(', ');
    
    let prompt = `Show me top and worst performing ads by ${metricLabel} over the last ${timeframeLabel} with ${groupingLabel} breakdown. `;
    prompt += `Selected metrics: ${selectedMetricsList}. `;

    if (period) {
      const periodDate = new Date(period);
      const periodLabel = selectedGrouping === 'day' 
        ? periodDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : selectedGrouping === 'week'
        ? `week of ${periodDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
        : periodDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      
      prompt += `Focus on ${periodLabel} performance. `;
    }

    // Add specific metric focus
    switch (metric) {
      case 'ctr':
        prompt += `Include creative elements and targeting that drive high/low CTR.`;
        break;
      case 'cvr':
        prompt += `Include landing page strategy and messaging that drive high/low CVR.`;
        break;
      case 'roas':
        prompt += `Include budget allocation and creative strategy that drive high/low ROAS.`;
        break;
      case 'spend':
        prompt += `Include cost structure and scaling strategy that drive efficient/inefficient spend.`;
        break;
      case 'impressions':
        prompt += `Include targeting and placement that drive high/low reach.`;
        break;
      case 'clicks':
        prompt += `Include creative elements and messaging that drive high/low clicks.`;
        break;
      case 'cpa':
        prompt += `Include conversion strategy and targeting that drive low/high CPA.`;
        break;
      case 'cpm':
        prompt += `Include targeting and creative quality that drive low/high CPM.`;
        break;
      default:
        prompt += `Include creative elements and performance metrics.`;
    }

    return prompt;
  };

  // Handle chat button click for specific metric (overview tab pattern)
  const handleMetricChat = (metric) => {
    let prompt;
    
    // Get selected metrics context for all prompts
    const metricLabels = {
      'ctr': 'CTR',
      'cvr': 'CVR', 
      'roas': 'ROAS',
      'cpa': 'CPA',
      'cpm': 'CPM',
      'spend': 'spend',
      'impressions': 'impressions',
      'clicks': 'clicks'
    };
    const selectedMetricsList = selectedMetrics.map(m => metricLabels[m] || m).join(', ');
    
    if (metric === 'trends') {
      // Make trends prompt dynamic based on selected metrics
      const primaryMetric = selectedMetrics[0]; // Use first selected metric as primary focus
      const primaryMetricLabel = metricLabels[primaryMetric] || primaryMetric;
      prompt = `Show me top and worst performing ads by ${primaryMetricLabel} trends over the last ${timeframes.find(t => t.key === selectedTimeframe)?.label || selectedTimeframe} with ${groupingOptions.find(g => g.key === selectedGrouping)?.label || selectedGrouping} breakdown. Selected metrics: ${selectedMetricsList}. Include creative elements and performance patterns for ${primaryMetricLabel}.`;
    } else if (metric === 'comparison') {
      prompt = `Show me top and worst performing ads by metric comparison over the last ${timeframes.find(t => t.key === selectedTimeframe)?.label || selectedTimeframe} with ${groupingOptions.find(g => g.key === selectedGrouping)?.label || selectedGrouping} breakdown. Selected metrics: ${selectedMetricsList}. Include creative elements and performance differences.`;
    } else {
      prompt = generateChatPrompt(metric);
    }
    
    // Auto-populate the chat input and open chat (this will be handled by parent Dashboard component)
    if (onFilterChange && typeof onFilterChange === 'function') {
      onFilterChange({ type: 'chat', message: prompt });
    }
  };

  // Handle chat button click for specific time period (overview tab pattern)
  const handlePeriodChat = (period) => {
    const prompt = generateChatPrompt(selectedMetrics[0], period);
    // Auto-populate the chat input and open chat (this will be handled by parent Dashboard component)
    if (onFilterChange && typeof onFilterChange === 'function') {
      onFilterChange({ type: 'chat', message: prompt });
    }
  };

  const formatValue = (value, format) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    
    switch (format) {
      case 'percentage':
        // SQL already returns percentages (e.g., 1.5 for 1.5%), so don't multiply by 100
        return `${value.toFixed(2)}%`;
      case 'currency':
        return `$${value.toLocaleString()}`;
      case 'multiplier':
        return `${value.toFixed(2)}x`;
      case 'number':
        return value.toLocaleString();
      default:
        return value.toFixed(2);
    }
  };

  const getMetricFormat = (metricKey) => {
    const metric = availableMetrics.find(m => m.key === metricKey);
    return metric ? metric.format : 'number';
  };

  const getMetricColor = (metricKey) => {
    const metric = availableMetrics.find(m => m.key === metricKey);
    return metric ? metric.color : '#6B7280';
  };

  // Load data when component mounts or dependencies change
  useEffect(() => {
    console.log('TimeTrends: Component mounted, accountId:', accountId);
    console.log('TimeTrends: useEffect triggered with dependencies:', { accountId, selectedTimeframe, selectedGrouping, selectedMetrics });
    
    if (accountId) {
      console.log('TimeTrends: Calling loadTimeSeriesData...');
      loadTimeSeriesData();
    } else {
      console.log('TimeTrends: No accountId, using sample data');
      // Force sample data if no accountId
      const { startDate, endDate } = getDateRange();
      const sampleData = generateSampleTimeSeriesData(startDate, endDate, selectedGrouping, selectedMetrics);
      setTimeSeriesData(sampleData);
      setError('Using sample data - no account ID available');
    }
  }, [accountId, selectedTimeframe, selectedGrouping, selectedMetrics]);

  // Force load data on mount regardless of dependencies
  useEffect(() => {
    console.log('TimeTrends: Component mounted, forcing data load...');
    const { startDate, endDate } = getDateRange();
    const sampleData = generateSampleTimeSeriesData(startDate, endDate, selectedGrouping, selectedMetrics);
    setTimeSeriesData(sampleData);
    console.log('TimeTrends: Sample data generated:', sampleData);
  }, []); // Empty dependency array - run only on mount

  const renderTrendsChart = () => {
    if (!timeSeriesData.length) return <div className="text-center text-slate-400 py-8">No data available</div>;
    
    return (
      <div className="space-y-6">
        {/* Multi-metric line chart */}
        <div className="bg-slate-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Performance Trends Over Time</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleMetricChat('trends')}
                className="p-2 text-slate-400 hover:text-blue-400 transition-colors rounded-lg hover:bg-slate-700/50"
                title="Ask AI about overall trends"
              >
                <MessageCircle className="h-5 w-5" />
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <RechartsLineChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="period" 
                stroke="#9CA3AF"
                tickFormatter={(value) => {
                  if (selectedGrouping === 'day') {
                    return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  } else if (selectedGrouping === 'week') {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  } else {
                    return new Date(value).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                  }
                }}
              />
              <YAxis stroke="#9CA3AF" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
                formatter={(value, name) => [
                  formatValue(value, getMetricFormat(name)),
                  availableMetrics.find(m => m.key === name)?.label || name
                ]}
                labelFormatter={(label) => {
                  const date = new Date(label);
                  return date.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  });
                }}
              />
              {selectedMetrics.map((metric, index) => (
                <Line
                  key={metric}
                  type="monotone"
                  dataKey={metric}
                  stroke={getMetricColor(metric)}
                  strokeWidth={2}
                  dot={{ fill: getMetricColor(metric), strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: getMetricColor(metric), strokeWidth: 2 }}
                />
              ))}
            </RechartsLineChart>
          </ResponsiveContainer>
        </div>

        {/* Summary statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {selectedMetrics.map((metric) => {
            const metricData = availableMetrics.find(m => m.key === metric);
            const values = timeSeriesData.map(d => d[metric]).filter(v => v !== null && v !== undefined && !isNaN(v));
            const avgValue = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
            const trend = values.length > 1 ? (values[values.length - 1] - values[0]) / values[0] : 0;
            
            return (
              <div key={metric} className="bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">{metricData?.label}</span>
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      trend > 0 ? 'bg-green-500/20 text-green-400' : 
                      trend < 0 ? 'bg-red-500/20 text-red-400' : 
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {trend > 0 ? '↗' : trend < 0 ? '↘' : '→'} {Math.abs(trend * 100).toFixed(1)}%
                    </span>
                    <button
                      onClick={() => handleMetricChat(metric)}
                      className="p-1 text-slate-400 hover:text-blue-400 transition-colors"
                      title="Ask AI about this metric"
                    >
                      <MessageCircle className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="text-2xl font-bold text-white">
                  {formatValue(avgValue, metricData?.format)}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Average over {timeSeriesData.length} periods
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderComparisonChart = () => {
    if (!timeSeriesData.length) return <div className="text-center text-slate-400 py-8">No data available</div>;
    
    // Group data by metric for comparison
    const comparisonData = selectedMetrics.map(metric => {
      const values = timeSeriesData.map(d => d[metric]).filter(v => v !== null && v !== undefined && !isNaN(v));
      const avgValue = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
      const maxValue = Math.max(...values);
      const minValue = Math.min(...values);
      
      return {
        metric: availableMetrics.find(m => m.key === metric)?.label || metric,
        average: avgValue,
        max: maxValue,
        min: minValue,
        color: getMetricColor(metric)
      };
    });

    return (
      <div className="space-y-6">
        <div className="bg-slate-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Metric Performance Comparison</h3>
            <button
              onClick={() => handleMetricChat('comparison')}
              className="p-2 text-slate-400 hover:text-blue-400 transition-colors rounded-lg hover:bg-slate-700/50"
              title="Ask AI about metric comparison"
            >
              <MessageCircle className="h-5 w-5" />
            </button>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={comparisonData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="metric" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
                formatter={(value, name) => [
                  formatValue(value, 'number'),
                  name === 'average' ? 'Average' : name === 'max' ? 'Maximum' : 'Minimum'
                ]}
              />
              <Bar dataKey="average" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="max" fill="#10B981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="min" fill="#EF4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        <span className="ml-3 text-slate-400">Loading time trends data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Banner - Show at top if there's an error */}
      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 text-yellow-400">
            <span className="text-sm">⚠️ {error}</span>
            <button 
              onClick={loadTimeSeriesData}
              className="text-xs px-2 py-1 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 rounded transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Header Controls */}
      <div className="bg-slate-800 rounded-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-white">Time Trends Analysis</h2>
            <p className="text-slate-400 text-sm">Analyze performance trends over time with multiple metrics</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Manual Refresh Button */}
            <button
              onClick={() => {
                console.log('TimeTrends: Manual refresh clicked');
                if (accountId) {
                  loadTimeSeriesData();
                } else {
                  const { startDate, endDate } = getDateRange();
                  const sampleData = generateSampleTimeSeriesData(startDate, endDate, selectedGrouping, selectedMetrics);
                  setTimeSeriesData(sampleData);
                  setError('Using sample data - no account ID available');
                }
              }}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
            >
              🔄 Refresh Data
            </button>
            
            {/* View Mode Selector */}
            <div className="flex items-center space-x-2">
              {viewModes.map((mode) => (
                <button
                  key={mode.key}
                  onClick={() => setViewMode(mode.key)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all ${
                    viewMode === mode.key
                      ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                      : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                  }`}
                >
                  {mode.icon}
                  <span className="text-sm font-medium">{mode.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Timeframe Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Time Range</label>
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
            >
              {timeframes.map((timeframe) => (
                <option key={timeframe.key} value={timeframe.key}>
                  {timeframe.label}
                </option>
              ))}
            </select>
          </div>

          {/* Grouping Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Grouping</label>
            <select
              value={selectedGrouping}
              onChange={(e) => setSelectedGrouping(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
            >
              {groupingOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.icon} {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Metrics Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Metrics</label>
            <select
              multiple
              value={selectedMetrics}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions, option => option.value);
                setSelectedMetrics(values);
              }}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
            >
              {availableMetrics.map((metric) => (
                <option key={metric.key} value={metric.key}>
                  {metric.icon} {metric.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Charts */}
      {viewMode === 'trends' && renderTrendsChart()}
      {viewMode === 'comparison' && renderComparisonChart()}

      {/* Chat Prompt Modal */}
      {/* This section is removed as per the new_code, as the chat functionality is now handled by the parent Dashboard component */}
    </div>
  );
};

export default TimeTrends;
